import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components/macro';
import { Icon, LoadingDot } from 'components/atoms';
import { transparentize, darken, lighten } from 'polished';
import { applyStyleModifiers } from 'styled-components-modifiers';
import { themeType } from 'types';

const BUTTON_MODIFIERS = {
  fixSpacing: () => `
    padding: 0;
  `,
  small: ({ theme }) => `
    padding: ${theme.spacing.xs2};
  `,
  xs3: ({ theme }) => `
    padding: ${theme.spacing.xs3};
  `,
  xs4: ({ theme }) => `
    padding: ${theme.spacing.xs4};
  `,
  smallText: ({ theme }) => `
    font-size: ${theme.sizes.s};
  `,
  xsText: ({ theme }) => `
    font-size: ${theme.sizes.xs};
  `,
  borderless: () => `
    border: none;
  `,
  leftAlign: () => `
    justify-content: start;
  `,
  spaceBetween: () => `
    justify-content: space-between;
  `,
  removeBorders: () => `
    border: 0px;
  `,
  bottomBorder: ({ theme }) => `
    border: 0;
    border-bottom: 1px solid ${theme.colors.borderLighter};
  `,
  removeBottomBorder: () => `
    border-bottom: 0px;
  `,
  capitalize: ({ theme }) => `
    text-transform: uppercase;
    font-size: ${theme.sizes.s};
  `,
  smaller: ({ theme }) => `
    padding: ${theme.spacing.xs2};
    font-size: ${theme.sizes.xs};
  `,
  stretch: () => `
    width: 100%;
    justify-content: center;
  `,
  stretchY: () => `
    height: 100%;
  `,
  alignLeft: () => `
    justify-content: left;
  `,
  marginRight: ({ theme }) => `
    margin-right: ${theme.spacing.xs};
  `,
  primary: ({ theme }) => `
    background-color: ${darken(0.01, theme.colors.primaryDarker)};
    color: ${theme.colors.white};
    border: ${theme.values.borderSize} solid ${darken(
  0.01,
  theme.colors.primaryDarker
)};
    svg {
      fill: ${theme.colors.baseInverse};
    }
    &:hover {
      background-color: ${darken(0.04, theme.colors.primaryDarker)};
      color: ${theme.colors.white};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.white};
      background-color: ${darken(0.03, theme.colors.primaryDarker)};
    }
    &:active {
      background-color: ${theme.colors.primaryDarker};
    }
  `,
  light: ({ theme }) => `
    color: ${theme.colors.baseLighter};
  `,
  danger: ({ theme }) => `
    background-color: ${darken(0.01, theme.colors.redDarker)};
    color: ${theme.colors.baseInverse};

    &:hover {
      background-color: ${darken(0.04, theme.colors.redDarker)};
      color: ${theme.colors.baseInverse};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.baseInverse};
      background-color: ${darken(0.03, theme.colors.redDarker)};
    }
    &:active {
      background-color: ${theme.colors.redDarker};
    }
  `,
  dark: ({ theme }) => `
    background-color: ${theme.colors.bgInverse};
    color: ${theme.colors.baseInverse};
    border: ${theme.values.borderSize} solid ${lighten(
  0.01,
  theme.colors.baseInverse
)};
    &:hover {
      background-color: ${lighten(0.3, theme.colors.bgInverse)};
      color: ${theme.colors.baseInverse};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.baseInverse};
      background-color: ${lighten(0.5, theme.colors.bgInverse)};
    }
    &:active {
      background-color: ${theme.colors.bgDark};
    }
  `,
  disabledText: ({ theme }) => `
    color: ${theme.colors.grey};
    cursor: not-allowed;

    &:hover {
      color: ${theme.colors.grey};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.grey};
    }
    &:active {
      color: ${theme.colors.grey};
    }
  `,
  disabled: ({ theme }) => `
    background-color: ${theme.colors.bg};
    color: ${theme.colors.grey};
    cursor: not-allowed;

    &:hover {
      background-color: ${theme.colors.bg};
      color: ${theme.colors.grey};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.grey};
      background-color: ${theme.colors.bg};
    }
    &:active {
      color: ${theme.colors.grey};
      background-color: ${theme.colors.bg};
    }
  `,
  invisible: ({ theme }) => `
    background-color: transparent;
    color: ${theme.colors.baseInverse};
    border: none;
    &:hover {
      background-color: ${transparentize(
    0.8,
    darken(0.04, theme.colors.bgLighter)
  )};
      color: ${theme.colors.baseInverse};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.baseInverse};
      background-color: ${transparentize(0.9, darken(0.03, theme.colors.bg))};
    }
    &:active {
      background-color: ${transparentize(0.6, darken(0.03, theme.colors.bg))};
    }
  `,
  invisibleDarker: ({ theme }) => `
  background-color: transparent;
  color: ${theme.colors.baseInverse};
  border: none;
  &:hover {
    background-color: ${transparentize(
    0.8,
    darken(0.15, theme.colors.bgLighter)
  )};
      color: ${theme.colors.baseInverse};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.baseInverse};
      background-color: ${transparentize(0.9, darken(0.1, theme.colors.bg))};
    }
    &:active {
      background-color: ${transparentize(0.6, darken(0.1, theme.colors.bg))};
    }
  `,
  invisibleHover: () => `
    &:hover {
      background-color: transparent;
    };
    &:focus {
      outline: 0;
      background-color: transparent;
    }
    &:active {
      background-color: transparent;
    }
  `,
  invisibleWithHover: ({ theme }) => `
    background-color: transparent;
    color: ${theme.colors.baseInverse};
    border: none;
  `,
  bgLighterSHover: ({ theme }) => `
    &:hover {
      background-color: ${darken(0.04, theme.colors.bgLighterS)};
      color: ${theme.colors.base};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.base};
      background-color: ${darken(0.03, theme.colors.bgLighterS)};
    }
    &:active {
      color: ${theme.colors.base};
      background-color: ${theme.colors.bgLighterS};
    }
  `,
  bgLighterS: ({ theme }) => `
    background-color: ${theme.colors.bgLighterS};
    color: ${theme.colors.base};

    &:hover {
      background-color: ${darken(0.04, theme.colors.bgLighterS)};
      color: ${theme.colors.base};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.base};
      background-color: ${darken(0.03, theme.colors.bgLighterS)};
    }
    &:active {
      color: ${theme.colors.base};
      background-color: ${theme.colors.bgLighterS};
    }
  `,
  contrast: ({ theme }) => `
    background-color: ${theme.colors.bg};
    color: ${theme.colors.base};
    border: ${theme.colors.bg} solid 1px;

    &:hover {
      background-color: ${theme.colors.bgDark};
      border: ${theme.colors.bgDark} solid 1px;
      color: ${theme.colors.baseInverse};
    }
    &:focus {
      outline: 0;
      background-color: ${darken(0.04, theme.colors.bgLighter)};
      border: ${darken(0.04, theme.colors.bgLighter)} solid 1px;
      color: ${theme.colors.base};
    }
    &:active {
      background-color: ${darken(0.03, theme.colors.bgLighter)};
      border: ${darken(0.03, theme.colors.bgLighter)} solid 1px;
      color: ${theme.colors.base};
    }
  `,
  fixHeight: () => `
    height: fit-content;
  `,
  fixedWidthMedium: () => `
    min-width: 110px;
    justify-content: center;
  `
};

const ButtonMenu = ({
  children,
  theme,
  onClick,
  className,
  modifiers,
  icon,
  type,
  loading,
  title,
  newItemText,
  ...props
}) => {
  const [isHover, setHover] = useState(false)
  const iconColor = useMemo(() => {
    if (modifiers.includes('disabled') || modifiers === 'disabled') return theme.colors.grey;
    if (modifiers.includes('disabledText') || modifiers === 'disabledText') return theme.colors.grey;
    if (modifiers.includes('contrast') || modifiers === 'contrast') return isHover ? theme.colors.baseInverse : theme.colors.base;
    if (modifiers.includes('dark') || modifiers === 'dark') return theme.colors.baseInverse
    if (modifiers.includes('active') || modifiers === 'active') return theme.colors.base;
    if (modifiers.includes('bgLighterS') || modifiers === 'active') return theme.colors.base;
    if (modifiers.includes('light') || modifiers === 'light') return theme.colors.baseLighter;
    return theme.colors.grey;
  }, [modifiers, theme, isHover]);

  const loadingDotModifiers = useMemo(() => {
    if (modifiers.includes('dark') || modifiers === 'dark') return ['inverse']
    if (modifiers.includes('primary') || modifiers === 'primary') return ['inverse']
    return []
  }, [modifiers])
  return (
    <Button
      type={type}
      onClick={e => {
        if (loading) return;
        onClick(e);
      }}
      className={className}
      modifiers={modifiers}
      loading={loading}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...props}
    >

      {icon && !newItemText && (
        <Button.Icon hasSpacing={Boolean(children || title)}>
          <Icon icon={icon} color={iconColor} height={16} />
        </Button.Icon>
      )}
      {loading ? (
        <ButtonLoading>
          <LoadingDot className="three-dots" modifiers={loadingDotModifiers} />
          <span className="children">{children}</span>
        </ButtonLoading>
      ) : (
        children
      )}
      {icon && newItemText && (
        <Button.Icon hasSpacing={Boolean(children || title)}>
          <Icon icon={icon} color={iconColor} height={16} />
        </Button.Icon>
      )}

    </Button>
  );
};

/* eslint-disable-next-line no-unused-vars */
const Button = styled(({ loading, type, ...rest }) => (
  /* eslint-disable-next-line react/button-has-type */
  <button type={type} {...rest} />
))`
  position: relative;
  display: inline-flex;
  align-items: center;
  color: ${props => props.theme.colors.grey};
  padding: ${props => `${props.theme.spacing.xs} ${props.theme.spacing.sm}`};
  font-weight: ${props => props.theme.weight.basex};
  color: ${props => props.theme.colors.base};
  background: ${props => props.theme.colors.bgLighter};
  border: ${props => props.theme.values.borderSize} solid
    ${props => props.theme.colors.borderBtn};
  cursor: ${props => (props.loading ? 'progress' : 'pointer')};
  transition: background 120ms ease-in-out;

  &:hover {
    background-color: ${props => darken(0.04, props.theme.colors.bgLighter)};
    color: ${props => props.theme.colors.base};
  }
  &:focus {
    outline: 0;
    color: ${props => props.theme.colors.base};
    background-color: ${props => darken(0.03, props.theme.colors.bg)};
  }
  &:active {
    background-color: ${props => props.theme.colors.bgLighter}};
  }

  ${applyStyleModifiers(BUTTON_MODIFIERS)};
`;

Button.propTypes = {
  loading: PropTypes.bool.isRequired,
};

Button.Icon = styled.div`
  display: flex;
  align-items: center;
  padding-right: ${props => (props.hasSpacing ? props.theme.spacing.xs3 : '0px')};
`;

ButtonMenu.propTypes = {
  children: PropTypes.node,
  theme: themeType.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  type: PropTypes.string,
  icon: PropTypes.string,
  modifiers: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  loading: PropTypes.bool,
  title: PropTypes.string,
  newItemText: PropTypes.string,
};

ButtonMenu.defaultProps = {
  children: null,
  onClick: () => null,
  className: '',
  modifiers: '',
  icon: null,
  type: 'button',
  loading: false,
  title: null,
  newItemText: '',
};

const ButtonLoading = styled.span`
  & .three-dots {
    position: absolute;
    left: 50%;
    transform: translate(-50%);
    margin-left: 3px;
  }

  & .children {
    visibility: hidden;
`;

export default withTheme(ButtonMenu);
